import { addDays, addHours, format, parseISO, startOfDay } from 'date-fns'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'

import { toDateKey } from './dispatch-detail'

type TimeRestriction = {
  startAt: string | null
  endAt: string | null
}

type TimeRestrictionResult = {
  isOutOfDeparture: boolean
  isOutOfArrival: boolean
}

/**
 * 時間が制限範囲内かどうかを判定する
 * @param time 判定対象の時間
 * @param restriction 制限時間の範囲
 * @returns 制限範囲外の場合はtrue
 */
export const isTimeOutOfRestriction = (
  time: string | null,
  restriction: TimeRestriction
): boolean => {
  if (!time || !restriction.startAt || !restriction.endAt) {
    return false
  }

  try {
    const targetTime = parseISO(time)
    const restrictionStart = parseISO(restriction.startAt)
    const restrictionEnd = parseISO(restriction.endAt)

    const targetTimeOnly = targetTime.getHours() * 60 + targetTime.getMinutes()
    const restrictionStartTimeOnly =
      restrictionStart.getHours() * 60 + restrictionStart.getMinutes()
    const restrictionEndTimeOnly =
      restrictionEnd.getHours() * 60 + restrictionEnd.getMinutes()

    return (
      targetTimeOnly < restrictionStartTimeOnly ||
      targetTimeOnly > restrictionEndTimeOnly
    )
  } catch (error) {
    console.error(
      // eslint-disable-next-line no-restricted-syntax
      '[isTimeOutOfRestriction] 時間制限チェックでエラー発生',
      error
    )
    return true // エラー時も範囲外とみなす
  }
}

/**
 * 配送詳細の時間が制限範囲内かどうかを判定する
 * @param detailStartAt 配送詳細の開始時間
 * @param detailEndAt 配送詳細の終了時間
 * @param shipmentOrder 配送依頼
 * @returns 発時間と着時間の制限範囲外の判定結果
 */
export const checkDispatchDetailTimeRestriction = (
  detailStartAt: string | null,
  detailEndAt: string | null,
  shipmentOrder: {
    departureTimeRestrictionStart: string | null
    departureTimeRestrictionEnd: string | null
    arrivalTimeRestrictionStart: string | null
    arrivalTimeRestrictionEnd: string | null
  }
): TimeRestrictionResult => {
  // 発時間制限のチェック
  const isOutOfDeparture = isTimeOutOfRestriction(detailStartAt, {
    startAt: shipmentOrder.departureTimeRestrictionStart,
    endAt: shipmentOrder.departureTimeRestrictionEnd
  })

  // 着時間制限のチェック
  const isOutOfArrival = isTimeOutOfRestriction(detailEndAt, {
    startAt: shipmentOrder.arrivalTimeRestrictionStart,
    endAt: shipmentOrder.arrivalTimeRestrictionEnd
  })

  return { isOutOfDeparture, isOutOfArrival }
}

/**
 * 配送詳細の時間が制限範囲内かどうかを判定する
 * @param detailStartAt 配送詳細の開始時間
 * @param detailEndAt 配送詳細の終了時間
 * @param shipmentOrder 配送依頼
 * @returns 制限範囲外の場合はtrue
 */
export const isDispatchDetailTimeOutOfRestriction = (
  detailStartAt: string | null,
  detailEndAt: string | null,
  shipmentOrder: {
    departureTimeRestrictionStart: string | null
    departureTimeRestrictionEnd: string | null
    arrivalTimeRestrictionStart: string | null
    arrivalTimeRestrictionEnd: string | null
  }
): boolean => {
  const { isOutOfDeparture, isOutOfArrival } =
    checkDispatchDetailTimeRestriction(
      detailStartAt,
      detailEndAt,
      shipmentOrder
    )
  // 両方の時間制限がある場合は、両方とも制限内である必要がある
  // どちらか片方でも制限外の場合は配車不可
  return isOutOfDeparture || isOutOfArrival
}

/**
 * 時間制限に基づいて適切な開始時間と終了時間を計算する
 * @param proposedStartAt 提案された開始時間
 * @param proposedEndAt 提案された終了時間
 * @param shipmentOrder 配送依頼
 * @returns 調整された開始時間と終了時間
 */
export const adjustTimeForRestrictions = (
  proposedStartAt: string,
  proposedEndAt: string,
  shipmentOrder: {
    departureTimeRestrictionStart: string | null
    departureTimeRestrictionEnd: string | null
    arrivalTimeRestrictionStart: string | null
    arrivalTimeRestrictionEnd: string | null
  }
): { startAt: string; endAt: string; isAdjusted: boolean } => {
  // 時間制限がない場合は提案された時間をそのまま返す
  if (
    !shipmentOrder.departureTimeRestrictionStart &&
    !shipmentOrder.arrivalTimeRestrictionStart
  ) {
    return { startAt: proposedStartAt, endAt: proposedEndAt, isAdjusted: false }
  }

  const proposedStart = parseISO(proposedStartAt)
  const proposedEnd = parseISO(proposedEndAt)

  let adjustedStart = proposedStart
  let adjustedEnd = proposedEnd
  let wasAdjusted = false

  // 発時間制限の確認と調整
  if (
    shipmentOrder.departureTimeRestrictionStart &&
    shipmentOrder.departureTimeRestrictionEnd
  ) {
    const depStart = parseISO(shipmentOrder.departureTimeRestrictionStart)
    const depEnd = parseISO(shipmentOrder.departureTimeRestrictionEnd)

    // 発地制限の時刻を提案された開始時間の日付に適用
    const depStartAdjusted = new Date(proposedStart)
    depStartAdjusted.setHours(
      depStart.getHours(),
      depStart.getMinutes(),
      depStart.getSeconds(),
      depStart.getMilliseconds()
    )

    const depEndAdjusted = new Date(proposedStart)
    depEndAdjusted.setHours(
      depEnd.getHours(),
      depEnd.getMinutes(),
      depEnd.getSeconds(),
      depEnd.getMilliseconds()
    )

    // 提案された開始時間が制限外の場合
    if (proposedStart < depStartAdjusted || proposedStart > depEndAdjusted) {
      // 制限開始時間に調整
      adjustedStart = depStartAdjusted
      wasAdjusted = true
    }
  }

  // 着時間制限の確認と調整
  if (
    shipmentOrder.arrivalTimeRestrictionStart &&
    shipmentOrder.arrivalTimeRestrictionEnd
  ) {
    const arrStart = parseISO(shipmentOrder.arrivalTimeRestrictionStart)
    const arrEnd = parseISO(shipmentOrder.arrivalTimeRestrictionEnd)

    // 着地制限の時刻を提案された終了時間の日付に適用
    const arrStartAdjusted = new Date(proposedEnd)
    arrStartAdjusted.setHours(
      arrStart.getHours(),
      arrStart.getMinutes(),
      arrStart.getSeconds(),
      arrStart.getMilliseconds()
    )

    const arrEndAdjusted = new Date(proposedEnd)
    arrEndAdjusted.setHours(
      arrEnd.getHours(),
      arrEnd.getMinutes(),
      arrEnd.getSeconds(),
      arrEnd.getMilliseconds()
    )

    // 提案された終了時間が制限外の場合
    if (proposedEnd < arrStartAdjusted || proposedEnd > arrEndAdjusted) {
      // 制限開始時間に調整
      adjustedEnd = arrStartAdjusted
      wasAdjusted = true
    }
  }

  return {
    startAt: adjustedStart.toISOString(),
    endAt: adjustedEnd.toISOString(),
    isAdjusted: wasAdjusted
  }
}

// 日またぎ（発日 < 着日）＋時間制限なし時のデフォルト描画時刻（fallback）
// 拠点単位の PreLoadDefaultTimeSetting が引き渡されない場合の従来挙動を維持する。
const CROSS_DAY_DEFAULT_START_HOUR = 17
const CROSS_DAY_DEFAULT_END_HOUR = 8

export type PreLoadDcDefaults = {
  pickupTime: string
  deliveryTime: string
  autoApply: boolean
}

export const parseHmToHoursMinutes = (
  hm: string | undefined,
  fallbackHour: number
): { hours: number; minutes: number } => {
  if (!hm) return { hours: fallbackHour, minutes: 0 }
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hm)
  if (!match) return { hours: fallbackHour, minutes: 0 }
  return { hours: Number(match[1]), minutes: Number(match[2]) }
}

/**
 * 発日と着日が異なる日付かどうかを判定する
 */
export const isCrossDayShipment = (
  shippingDate: Date | string | null | undefined,
  deliveryDate: Date | string | null | undefined
): boolean => {
  if (!shippingDate || !deliveryDate) return false
  return toDateKey(shippingDate) !== toDateKey(deliveryDate)
}

/**
 * 日またぎ配送のデフォルト時刻を返す
 * 拠点単位の PreLoadDefaultTimeSetting (dcDefaults) があればその値を使い、
 * なければ fallback の CROSS_DAY_DEFAULT_*_HOUR (17:00 / 08:00) を使う。
 */
export const getCrossDayDefaultTimes = (
  shippingDate: Date | string,
  deliveryDate: Date | string,
  dcDefaults?: Pick<PreLoadDcDefaults, 'pickupTime' | 'deliveryTime'>
): { startAt: Date; endAt: Date } => {
  const pickup = parseHmToHoursMinutes(
    dcDefaults?.pickupTime,
    CROSS_DAY_DEFAULT_START_HOUR
  )
  const delivery = parseHmToHoursMinutes(
    dcDefaults?.deliveryTime,
    CROSS_DAY_DEFAULT_END_HOUR
  )
  const s =
    typeof shippingDate === 'string'
      ? parseISO(shippingDate)
      : new Date(shippingDate)
  s.setHours(pickup.hours, pickup.minutes, 0, 0)
  const d =
    typeof deliveryDate === 'string'
      ? parseISO(deliveryDate)
      : new Date(deliveryDate)
  d.setHours(delivery.hours, delivery.minutes, 0, 0)
  return { startAt: s, endAt: d }
}

/**
 * 時間制限に基づいて配車時間を自動調整する関数
 * @param shipmentOrder 配送依頼データ
 * @param startAt 開始時間（ISO文字列）
 * @param endAt 終了時間（ISO文字列）
 * @param boardShippingDate 配車ボードの日付（ISO文字列）
 * @param deliveryTime デフォルト運行時間（時間）
 * @returns 調整された開始時間と終了時間
 */
export const autoAdjustForTimeRestrictions = (
  shipmentOrder: {
    departureTimeRestrictionStart?: string | null
    departureTimeRestrictionEnd?: string | null
    arrivalTimeRestrictionStart?: string | null
    arrivalTimeRestrictionEnd?: string | null
    group: {
      shippingDate?: string | null
      deliveryDate?: string | null
    } | null
  },
  startAt: string,
  endAt: string,
  boardShippingDate: string,
  deliveryTime: number,
  dcDefaults?: PreLoadDcDefaults
): { startAt: string; endAt: string; isAdjusted: boolean } => {
  let adjustedStartAt = startAt
  let adjustedEndAt = endAt
  let wasAdjusted = false

  const hasDepartureRestriction = !!(
    shipmentOrder.departureTimeRestrictionStart &&
    shipmentOrder.departureTimeRestrictionEnd
  )
  const hasArrivalRestriction = !!(
    shipmentOrder.arrivalTimeRestrictionStart &&
    shipmentOrder.arrivalTimeRestrictionEnd
  )

  // 発時間制限がある場合は発地制限開始時刻に調整
  if (hasDepartureRestriction) {
    const depStartOriginal = parseISO(
      shipmentOrder.departureTimeRestrictionStart!
    )
    const boardDate = parseISO(boardShippingDate)
    const depStart = new Date(boardDate)
    depStart.setHours(
      depStartOriginal.getHours(),
      depStartOriginal.getMinutes(),
      depStartOriginal.getSeconds(),
      depStartOriginal.getMilliseconds()
    )

    // 現在の開始時刻と異なる場合は調整フラグを立てる
    if (parseISO(startAt).getTime() !== depStart.getTime()) {
      wasAdjusted = true
    }
    // 常に制限開始時刻を使用
    adjustedStartAt = depStart.toISOString()

    // 着時間制限がない場合は、開始時刻からデフォルト運行時間後を終了時刻とする
    if (!hasArrivalRestriction) {
      adjustedEndAt = addHours(depStart, deliveryTime).toISOString()
    }
  }

  // 着時間制限がある場合は着地制限開始時刻に調整
  if (hasArrivalRestriction) {
    const arrStartOriginal = parseISO(
      shipmentOrder.arrivalTimeRestrictionStart!
    )

    if (
      shipmentOrder.group?.shippingDate &&
      shipmentOrder.group?.deliveryDate &&
      shipmentOrder.group?.shippingDate !== shipmentOrder.group?.deliveryDate
    ) {
      // 発送日と着日が異なる場合は着日の日付を使用
      const deliveryDate = parseISO(shipmentOrder.group.deliveryDate)
      const arrStart = new Date(deliveryDate)
      arrStart.setHours(
        arrStartOriginal.getHours(),
        arrStartOriginal.getMinutes(),
        arrStartOriginal.getSeconds(),
        arrStartOriginal.getMilliseconds()
      )

      // 現在の終了時刻と異なる場合は調整フラグを立てる
      if (parseISO(endAt).getTime() !== arrStart.getTime()) {
        wasAdjusted = true
      }
      // 常に制限開始時刻を使用
      adjustedEndAt = arrStart.toISOString()

      if (!hasDepartureRestriction) {
        /**
         * 着日と発送日が異なり発時間制限がない場合は、発日の23時を開始時刻とする
         * 意図：着時間制限の4時間前を開始時間にする必要がないため
         * 発日の末尾に配置して、配車盤上で調整してもらう
         */
        const boardDate = parseISO(boardShippingDate) // 配車盤の日付

        const adjustedStart = new Date(boardDate)
        adjustedStart.setHours(23, 0, 0, 0)
        adjustedStartAt = adjustedStart.toISOString()
      }
    } else {
      // 同一日の場合は配車ボードの日付を使用
      const boardDate = parseISO(boardShippingDate)
      const arrStart = new Date(boardDate)
      arrStart.setHours(
        arrStartOriginal.getHours(),
        arrStartOriginal.getMinutes(),
        arrStartOriginal.getSeconds(),
        arrStartOriginal.getMilliseconds()
      )

      // 現在の終了時刻と異なる場合は調整フラグを立てる
      if (parseISO(endAt).getTime() !== arrStart.getTime()) {
        wasAdjusted = true
      }
      // 常に制限開始時刻を使用
      adjustedEndAt = arrStart.toISOString()

      // 発時間制限がない場合は、終了時刻からデフォルト運行時間前 or 0時を下回る場合は0時を開始時刻とする
      if (!hasDepartureRestriction) {
        const candidateStart = addHours(arrStart, -deliveryTime)
        const dayStart = startOfDay(arrStart) // 同日の 00:00

        adjustedStartAt = (
          candidateStart < dayStart ? dayStart : candidateStart
        ).toISOString()
      }
    }
  }

  // 時間制限なし＋日またぎ：デフォルト描画（拠点設定値 or 17:00〜08:00 fallback）
  // autoApply === false なら強制リサイズせず、ドロップ位置の startAt / endAt を保持する。
  if (!hasDepartureRestriction && !hasArrivalRestriction) {
    const hasCrossDay = !!(
      shipmentOrder.group?.shippingDate &&
      shipmentOrder.group?.deliveryDate &&
      shipmentOrder.group.shippingDate !== shipmentOrder.group.deliveryDate
    )
    if (hasCrossDay && dcDefaults && dcDefaults.autoApply !== false) {
      const pickup = parseHmToHoursMinutes(
        dcDefaults?.pickupTime,
        CROSS_DAY_DEFAULT_START_HOUR
      )
      const delivery = parseHmToHoursMinutes(
        dcDefaults?.deliveryTime,
        CROSS_DAY_DEFAULT_END_HOUR
      )
      const boardDate = parseISO(boardShippingDate)
      const crossDayStart = new Date(boardDate)
      crossDayStart.setHours(pickup.hours, pickup.minutes, 0, 0)

      // 翌々着（N+2）以降も翌日着（N+1）と同じ span に揃えるため、着日は
      // 発送日+1日を上限にクリップする（#3928: クリップしないと矢羽 span が
      // 39h 級になり、後続のリサイズガードで縮小操作まで止まる）
      const deliveryDate = parseISO(shipmentOrder.group!.deliveryDate!)
      const maxDeliveryDate = addDays(boardDate, 1)
      const clippedDeliveryDate =
        deliveryDate > maxDeliveryDate ? maxDeliveryDate : deliveryDate
      const crossDayEnd = new Date(clippedDeliveryDate)
      crossDayEnd.setHours(delivery.hours, delivery.minutes, 0, 0)

      if (
        parseISO(startAt).getTime() !== crossDayStart.getTime() ||
        parseISO(endAt).getTime() !== crossDayEnd.getTime()
      ) {
        wasAdjusted = true
      }
      adjustedStartAt = crossDayStart.toISOString()
      adjustedEndAt = crossDayEnd.toISOString()
    }
  }

  return {
    startAt: adjustedStartAt,
    endAt: adjustedEndAt,
    isAdjusted: wasAdjusted
  }
}

/** {@link deriveAdjustedTimesPerOrder} に渡す配送依頼の最小形 */
export type ShipmentOrderForTimeAdjustment = {
  id: number
  departureTimeRestrictionStart?: string | null
  departureTimeRestrictionEnd?: string | null
  arrivalTimeRestrictionStart?: string | null
  arrivalTimeRestrictionEnd?: string | null
  group: {
    shippingDate?: string | null
    deliveryDate?: string | null
  } | null
}

export type OrderTime = {
  shipmentOrderId: number
  startAt: string
  endAt: string
}

export type DeriveAdjustedTimesPerOrderResult = {
  /** 依頼ごとの補正後 startAt/endAt */
  orderTimes: OrderTime[]
  /** いずれかの依頼で補正が行われたか（トースト表示の判定用） */
  isAnyAdjusted: boolean
  /** 全 orderTimes の min(startAt) / max(endAt)。dto の代表値・日不一致判定の起点に使う */
  envelope: { startAt: string; endAt: string }
}

/**
 * 発着時間制限の有無・日またぎの有無を判定する（{@link deriveAdjustedTimesPerOrder} /
 * {@link deriveAdjustedRangeForSingleOrder} で発火条件の算出に使う共通部分のみを切り出したもの。
 * 算出後の発火条件そのもの（autoApply の扱い等）は呼び出し側ごとに異なるため、
 * ここでは真偽値の算出だけを共有する）
 */
const deriveTimeRestrictionAndCrossDayFlags = (
  shipmentOrder: Omit<ShipmentOrderForTimeAdjustment, 'id'>
): { hasTimeRestrictions: boolean; hasCrossDay: boolean } => ({
  hasTimeRestrictions: !!(
    (shipmentOrder.departureTimeRestrictionStart &&
      shipmentOrder.departureTimeRestrictionEnd) ||
    (shipmentOrder.arrivalTimeRestrictionStart &&
      shipmentOrder.arrivalTimeRestrictionEnd)
  ),
  hasCrossDay: !!(
    shipmentOrder.group?.shippingDate &&
    shipmentOrder.group?.deliveryDate &&
    shipmentOrder.group.shippingDate !== shipmentOrder.group.deliveryDate
  )
})

/**
 * 複数依頼ドロップ（束ねカード・一括配車）の依頼ごと時間制限補正。
 *
 * 単一依頼ドロップ経路と同じ分岐（hasTimeRestrictions || 日またぎ かつ
 * autoApply !== false）を依頼ごとに適用し、制限が無い依頼はドロップ位置のまま返す。
 * envelope は BE へ送る group 代表値（明細ごとの時刻の min/max を BE が再導出する
 * のと同じ考え方）に使う。
 */
export const deriveAdjustedTimesPerOrder = (
  shipmentOrders: ShipmentOrderForTimeAdjustment[],
  dropStartAt: string,
  dropEndAt: string,
  fallbackBoardShippingDate: string,
  defaultShipmentDuration: number,
  dcDefaults?: PreLoadDcDefaults
): DeriveAdjustedTimesPerOrderResult => {
  let isAnyAdjusted = false

  const orderTimes: OrderTime[] = shipmentOrders.map((order) => {
    const { hasTimeRestrictions, hasCrossDay } =
      deriveTimeRestrictionAndCrossDayFlags(order)

    if (
      hasTimeRestrictions ||
      (hasCrossDay && dcDefaults?.autoApply !== false)
    ) {
      const boardShippingDate =
        order.group?.shippingDate ?? fallbackBoardShippingDate

      const adjusted = autoAdjustForTimeRestrictions(
        order,
        dropStartAt,
        dropEndAt,
        boardShippingDate,
        defaultShipmentDuration,
        dcDefaults
      )

      if (adjusted.isAdjusted) {
        isAnyAdjusted = true
      }

      return {
        shipmentOrderId: order.id,
        startAt: adjusted.startAt,
        endAt: adjusted.endAt
      }
    }

    return {
      shipmentOrderId: order.id,
      startAt: dropStartAt,
      endAt: dropEndAt
    }
  })

  // startAt/endAt を ms に一度だけ変換してから min/max を取る（reduce の
  // accumulator を毎回 parseISO し直さないようにするため）
  const orderTimesWithMs = orderTimes.map((t) => ({
    startAt: t.startAt,
    endAt: t.endAt,
    startMs: parseISO(t.startAt).getTime(),
    endMs: parseISO(t.endAt).getTime()
  }))

  const envelopeWithMs =
    orderTimesWithMs.length === 0
      ? null
      : orderTimesWithMs.reduce((acc, t) => ({
          startAt: t.startMs < acc.startMs ? t.startAt : acc.startAt,
          startMs: Math.min(acc.startMs, t.startMs),
          endAt: t.endMs > acc.endMs ? t.endAt : acc.endAt,
          endMs: Math.max(acc.endMs, t.endMs)
        }))

  const envelope = envelopeWithMs
    ? { startAt: envelopeWithMs.startAt, endAt: envelopeWithMs.endAt }
    : { startAt: dropStartAt, endAt: dropEndAt }

  return { orderTimes, isAnyAdjusted, envelope }
}

/**
 * 単一の未配車依頼ドロップの時間制限補正（{@link deriveAdjustedTimesPerOrder} の単一版）。
 *
 * onDragEnd の新規配車パターン（単一ドロップ）と同じ発火条件
 * `hasTimeRestrictions || (hasCrossDay && dcDefaults && dcDefaults.autoApply !== false)`
 * で {@link autoAdjustForTimeRestrictions} を呼ぶ。条件に当てはまらなければドロップ位置
 * （startAt/endAt）をそのまま返す。
 *
 * ドラッグ中プレビュー（onDragMove）とドロップ確定（onDragEnd）の両方から同じ条件で
 * 呼べるように切り出している。複数依頼版（deriveAdjustedTimesPerOrder）は
 * `dcDefaults?.autoApply !== false`（dcDefaults 未指定時に true 扱い）と発火条件が
 * 微妙に異なる既存の非対称があるため、ここでは単一パターンの条件をそのまま踏襲し、
 * 複数依頼版の条件を変更・統一しない。
 *
 * 単一依頼の呼び出しでは依頼IDを束ねる必要がないため、{@link ShipmentOrderForTimeAdjustment}
 * から id を除いた形を受け取る（呼び出し側で id を用意しなくてよい）。
 */
export const deriveAdjustedRangeForSingleOrder = (
  shipmentOrder: Omit<ShipmentOrderForTimeAdjustment, 'id'>,
  startAt: string,
  endAt: string,
  boardShippingDate: string,
  defaultShipmentDuration: number,
  dcDefaults?: PreLoadDcDefaults
): { startAt: string; endAt: string; isAdjusted: boolean } => {
  const { hasTimeRestrictions, hasCrossDay } =
    deriveTimeRestrictionAndCrossDayFlags(shipmentOrder)

  if (
    !hasTimeRestrictions &&
    !(hasCrossDay && dcDefaults && dcDefaults.autoApply !== false)
  ) {
    return { startAt, endAt, isAdjusted: false }
  }

  return autoAdjustForTimeRestrictions(
    shipmentOrder,
    startAt,
    endAt,
    boardShippingDate,
    defaultShipmentDuration,
    dcDefaults
  )
}

/**
 * 日またぎ→同日に遷移した際のfrom維持 + to再算出。
 * 現在のfromをそのまま維持し、to = from + defaultDurationHours で算出する。
 *
 * @param currentFrom 現在のfrom値（Date | string | null）
 * @param shippingDate 発日（Date | string）— 日付のベース
 * @param defaultDurationHours toの算出に使うデフォルト時間（時間単位）
 * @returns {startAt, endAt} 算出後の時刻。currentFromがnullの場合はnull
 */
export const getRestoredTimesFromCrossDay = (
  currentFrom: Date | string | null,
  shippingDate: Date | string,
  defaultDurationHours: number
): { startAt: Date; endAt: Date } | null => {
  const currentFromDate = currentFrom ? new Date(currentFrom) : null
  if (!currentFromDate || isNaN(currentFromDate.getTime())) return null

  const fromHours = currentFromDate.getHours()
  const fromMinutes = currentFromDate.getMinutes()

  const baseDate =
    typeof shippingDate === 'string'
      ? parseISO(shippingDate)
      : new Date(shippingDate)
  const startAt = new Date(baseDate)
  startAt.setHours(fromHours, fromMinutes, 0, 0)
  const endAt = new Date(baseDate)
  endAt.setHours(fromHours + defaultDurationHours, fromMinutes, 0, 0)
  // 同日内に収める（5分単位の上限: 23:55）
  const endOfDay = new Date(baseDate)
  endOfDay.setHours(23, 55, 0, 0)
  if (endAt > endOfDay) {
    endAt.setTime(endOfDay.getTime())
  }

  return { startAt, endAt }
}

/** {@link isAnyDetailTimeOutOfRestriction} / {@link describeTimeRestrictionViolations} に渡す明細の最小形 */
export type TimeRestrictionCheckDetail = {
  startAt: string
  endAt: string
  shipmentOrders: {
    departureTimeRestrictionStart: string | null
    departureTimeRestrictionEnd: string | null
    arrivalTimeRestrictionStart: string | null
    arrivalTimeRestrictionEnd: string | null
  }[]
}

/**
 * 矢羽根の移動・リサイズ確定前に時間制限外になるかを判定する（移動/リサイズは
 * 自動補正しない方針のための警告モーダル判定用）。
 * 束ね（複数明細）の一括シフト・着時刻リサイズにも使えるよう、明細は配列で受け取り
 * 1件でも制限外なら true を返す。単一明細の場合は要素数1の配列を渡す。
 */
export const isAnyDetailTimeOutOfRestriction = (
  details: TimeRestrictionCheckDetail[]
): boolean =>
  details.some((detail) =>
    detail.shipmentOrders.some((order) =>
      isDispatchDetailTimeOutOfRestriction(detail.startAt, detail.endAt, order)
    )
  )

/**
 * 制限外になった発着時間の内訳を HH:mm 表記の文字列配列で返す（警告モーダルの表示用）。
 * 同一内容の内訳は重複排除する。
 */
export const describeTimeRestrictionViolations = (
  details: TimeRestrictionCheckDetail[]
): string[] => {
  const reasons = new Set<string>()

  for (const detail of details) {
    for (const order of detail.shipmentOrders) {
      const { isOutOfDeparture, isOutOfArrival } =
        checkDispatchDetailTimeRestriction(detail.startAt, detail.endAt, order)

      if (
        isOutOfDeparture &&
        order.departureTimeRestrictionStart &&
        order.departureTimeRestrictionEnd
      ) {
        reasons.add(
          VALIDATION_MESSAGES.OUT_OF_DEPARTURE_TIME_RESTRICTION2(
            format(parseISO(order.departureTimeRestrictionStart), 'HH:mm'),
            format(parseISO(order.departureTimeRestrictionEnd), 'HH:mm')
          )
        )
      }

      if (
        isOutOfArrival &&
        order.arrivalTimeRestrictionStart &&
        order.arrivalTimeRestrictionEnd
      ) {
        reasons.add(
          VALIDATION_MESSAGES.OUT_OF_ARRIVAL_TIME_RESTRICTION2(
            format(parseISO(order.arrivalTimeRestrictionStart), 'HH:mm'),
            format(parseISO(order.arrivalTimeRestrictionEnd), 'HH:mm')
          )
        )
      }
    }
  }

  return Array.from(reasons)
}

/** ドラッグ中の対象が持つ、カーソル位置の範囲と補正後の着地範囲 */
export type DragRangePair = {
  range: { startAt: string; endAt: string } | null
  adjustedRange: { startAt: string; endAt: string } | null
}

/**
 * 発着時間制限の補正によって着地位置がカーソル位置から離れている場合に、その
 * 着地範囲を返す（離れていない・データ未確定なら null）。
 *
 * 離れている間は「カーソル上の見た目」と「実際に落ちる位置」が別物になるため、
 * 着地予測の描画・ドラッグ中の横スクロール追従・掴んでいるカードの見せ方の
 * いずれもこの判定を共通の起点にする。
 */
export const resolveDisplacedDragLanding = (
  value: DragRangePair | null
): { startAt: string; endAt: string } | null => {
  const { range, adjustedRange } = value ?? {}
  if (!range || !adjustedRange) return null
  if (
    range.startAt === adjustedRange.startAt &&
    range.endAt === adjustedRange.endAt
  ) {
    return null
  }
  return adjustedRange
}
